// src/components/VerticalDotsNav.tsx
import React, { useEffect, useRef, useState } from 'react';

interface VerticalDotsNavProps {
    language: 'en' | 'fr';
}

// Les identifiants des sections restent inchangés, seuls les libellés varient
const sectionsDataByLanguage = {
    en: [
        { id: 'accueil', label: 'Home' },
        { id: 'apropos', label: 'About me' },
        { id: 'cursus', label: 'Education / Experience' },
        { id: 'competences', label: 'Skills' },
        { id: 'qualites', label: 'Qualities' },
        { id: 'projets', label: 'Projects' },
    ],
    fr: [
        { id: 'accueil', label: 'Accueil' },
        { id: 'apropos', label: 'A propos de moi' },
        { id: 'cursus', label: 'Cursus / Expérience professionnelle' },
        { id: 'competences', label: 'Compétences' },
        { id: 'qualites', label: 'Qualités' },
        { id: 'projets', label: 'Projets' },
    ],
};

const VerticalDotsNav: React.FC<VerticalDotsNavProps> = ({ language }) => {
    // Sélection des sections en fonction de la langue
    const sectionsData = sectionsDataByLanguage[language];

    // État pour la section actuellement visible (active)
    const [activeSection, setActiveSection] = useState<string>(sectionsData[0].id);

    // Stockage des éléments DOM de chaque section
    const sectionRefs = useRef<(HTMLElement | null)[]>([]);

    // Au premier rendu, récupérer les éléments par leur id
    useEffect(() => {
        sectionRefs.current = sectionsData.map(({ id }) => document.getElementById(id));
    }, [sectionsData]);

    // Configuration de l’IntersectionObserver pour déterminer la section visible
    useEffect(() => {
        const observerOptions = {
            root: null,
            rootMargin: '0px',
            threshold: 0.6, // 60% visible => section active
        };

        const observerCallback = (entries: IntersectionObserverEntry[]) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setActiveSection(entry.target.id);
                }
            });
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);

        // Observer chaque section
        sectionRefs.current.forEach((el) => {
            if (el) observer.observe(el);
        });

        // Nettoyage
        return () => {
            observer.disconnect();
        };
    }, []);

    // Défilement doux au clic sur un point
    const handleClick = (id: string) => {
        const el = document.getElementById(id);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div
            style={{
                position: 'fixed',
                top: '50%',
                right: '20px',
                transform: 'translateY(-50%)',
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem',
                zIndex: 9999,
            }}
        >
            {sectionsData.map((section) => (
                <div
                    key={section.id}
                    onClick={() => handleClick(section.id)}
                    style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        cursor: 'pointer',
                        backgroundColor: activeSection === section.id ? '#FFA500' : '#ccecff',
                        transition: 'background-color 0.3s ease',
                    }}
                    title={section.label}
                />
            ))}
        </div>
    );
};

export default VerticalDotsNav;
